import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, TablePagination, Alert, Divider
} from '@mui/material';
import { Add, Search, Refresh, Edit, Delete, People, AccountBalance, Assignment, Visibility } from '@mui/icons-material';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { ContentCopy, Key } from '@mui/icons-material';

// Generates a random binder ID: 3 uppercase letters + 6 digits, e.g. KTX948271
// Serves as the universal account code tying all services together (SIM, PBX, CDR, billing)
function generateBinderId(): string {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits  = '0123456789';
  const alpha = Array.from({ length: 3 }, () => letters[Math.floor(Math.random() * letters.length)]).join('');
  const num   = Array.from({ length: 6 }, () => digits[Math.floor(Math.random() * digits.length)]).join('');
  return alpha + num;
}

const emptyForm: any = {
  name: '', code: '', contact_email: '', contact_phone: '',
  status: 'active', billing_type: 'prepaid', currency: 'USD',
  timezone: 'UTC', credit_limit: 0, tier: 'customer',
  rate_plan_id: '', routing_profile_id: '',
};

export const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState<any>(null);
  const [credsDialog, setCredsDialog] = useState(false);
  const [ratePlans, setRatePlans] = useState<any[]>([]);
  const [routingProfiles, setRoutingProfiles] = useState<any[]>([]);
  const [currencyUpdating, setCurrencyUpdating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(rowsPerPage), offset: String(page * rowsPerPage), tier: 'customer' });
      if (search) params.set('search', search);
      const { data: res } = await api.get(`/tenants?${params}`);
      setData(res.data || (Array.isArray(res) ? res : []));
      setTotal(res.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, page, rowsPerPage]);

  const loadLookups = async () => {
    try {
      const [rp, rop] = await Promise.all([
        api.get('/rates/plans?limit=200'),
        api.get('/routing/profiles?limit=200'),
      ]);
      setRatePlans(rp.data?.data || (Array.isArray(rp.data) ? rp.data : []));
      setRoutingProfiles(rop.data?.data || (Array.isArray(rop.data) ? rop.data : []));
    } catch (e) { console.error(e); }
  };

  const handleCurrencyChange = async (id: string, currency: string) => {
    setCurrencyUpdating(id);
    try {
      await api.put(`/tenants/${id}`, { currency });
      setData(prev => prev.map(r => r.id === id ? { ...r, currency } : r));
    } catch (e) { console.error(e); }
    finally { setCurrencyUpdating(null); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLookups(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, code: generateBinderId() });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({
      ...emptyForm, ...row,
      rate_plan_id: row.rate_plan_id || '',
      routing_profile_id: row.routing_profile_id || '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = { ...form, rate_plan_id: form.rate_plan_id || null, routing_profile_id: form.routing_profile_id || null };
      if (editing) await api.put(`/tenants/${editing.id}`, payload);
      else await api.post('/tenants', payload);
      // Rebuild routing so the customer's per-trunk routing group reflects the rate-plan link
      try { await api.post('/sbc/sync-routing'); } catch {}
      setDialogOpen(false);
      load();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editing) return;
    try { await api.delete(`/tenants/${editing.id}`); setDeleteDialogOpen(false); load(); }
    catch (e: any) { console.error(e); }
  };

  const statusColor = (s: string): any =>
    s === 'active' ? 'success' : s === 'suspended' ? 'warning' : 'default';

  const billingColor = (t: string): any =>
    t === 'prepaid' ? 'info' : t === 'postpaid' ? 'warning' : 'default';

  const planName = (row: any) => row.rate_plan?.name || (row.rate_plan_id ? row.rate_plan_id.slice(0, 8) : '—');
  const routeName = (row: any) => row.routing_profile?.name || (row.routing_profile_id ? row.routing_profile_id.slice(0, 8) : '—');

  const unassignedCount = data.filter(r => !r.rate_plan_id).length;

  // Assign a rate plan to a customer + rebuild routing (creates their per-customer routing group)
  const quickAssign = async (row: any, planId: string) => {
    if (!planId) return;
    try {
      await api.patch(`/tenants/${row.id}/rate-plan`, { rate_plan_id: planId });
      try { await api.post('/sbc/sync-routing'); } catch {}
      load();
    } catch (e: any) { setError(e.response?.data?.message || 'Assign failed'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <People sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Customers</Typography>
            <Typography variant="body2" color="text.secondary">{total} customers — manage rate plans and routing assignments</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Search customers..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 240 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Customer</Button>
        </Box>
      </Box>

      {!loading && unassignedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>{unassignedCount} customer(s) have no rate plan.</b> Without a rate plan their calls
          <b> can't be rated or routed</b> (the per-customer trunk routing needs it). Use the red
          <b> "⚠ Assign plan"</b> dropdown in the Rate Plan column below to fix each one.
        </Alert>
      )}

      <Card>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Customer', 'Code', 'Rate Plan', 'Routing Profile', 'Billing', 'Currency', 'Balance', 'Credit Limit', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                      {row.contact_email && <Typography variant="caption" color="text.secondary">{row.contact_email}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{row.code}</TableCell>
                    <TableCell>
                      {row.rate_plan_id ? (
                        <Tooltip title="Change rate plan (edit customer)">
                          <Chip label={planName(row)} size="small" color="primary" variant="outlined" icon={<Assignment fontSize="small" />} onClick={() => openEdit(row)} sx={{ cursor: 'pointer' }} />
                        </Tooltip>
                      ) : (
                        <TextField select size="small" value="" onChange={e => quickAssign(row, e.target.value)}
                          sx={{ minWidth: 150, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' }, '& .MuiSelect-select': { color: 'error.main', fontWeight: 600, py: 0.5 } }}
                          SelectProps={{ displayEmpty: true, renderValue: () => '⚠ Assign plan' }}>
                          {ratePlans.length === 0 && <MenuItem value="" disabled>No rate plans — create one first</MenuItem>}
                          {ratePlans.map((p: any) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                        </TextField>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.routing_profile_id ? (
                        <Chip label={routeName(row)} size="small" color="secondary" variant="outlined" />
                      ) : (
                        <Chip label="No route" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell><Chip label={row.billing_type} size="small" color={billingColor(row.billing_type)} /></TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      {currencyUpdating === row.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <TextField select size="small" value={row.currency || 'USD'}
                          onChange={e => handleCurrencyChange(row.id, e.target.value)}
                          variant="standard"
                          sx={{ minWidth: 70 }}
                          SelectProps={{ sx: { fontSize: 13, fontWeight: 600 } }}>
                          {['USD','AED','EUR','GBP','SAR'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: Number(row.balance) < 0 ? 'error.main' : 'success.main' }}>
                      {row.currency} {Number(row.balance || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{row.currency} {Number(row.credit_limit || 0).toFixed(2)}</TableCell>
                    <TableCell><Chip label={row.status} size="small" color={statusColor(row.status)} /></TableCell>
                    <TableCell>
                      <Tooltip title="View details"><IconButton size="small" color="primary" onClick={() => navigate('/customers/' + row.id)}><Visibility fontSize="small" /></IconButton></Tooltip><Tooltip title="Edit Customer"><IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Adjust Balance"><IconButton size="small" color="success" onClick={() => { setEditing(row); setAssignDialogOpen(true); }}><AccountBalance fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setEditing(row); setDeleteDialogOpen(true); }}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>No customers found. Click "Add Customer" to create one.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[25]} />
          </>
        )}
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Customer' : 'New Customer'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">BASIC INFO</Typography></Divider></Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Customer Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Binder ID (Account Code) *" value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                required
                helperText={editing ? 'Unique ID — ties all services (SIM, PBX, CDR, billing) together' : 'Auto-generated — ties all services together'}
                InputProps={{ endAdornment: !editing ? (
                  <InputAdornment position="end">
                    <Tooltip title="Generate new binder ID">
                      <IconButton size="small" onClick={() => setForm((f: any) => ({ ...f, code: generateBinderId() }))}>
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Contact Email" value={form.contact_email || ''} onChange={e => setForm({ ...form, contact_email: e.target.value })} type="email" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Contact Phone" value={form.contact_phone || ''} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
            </Grid>

            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">RATE PLAN & ROUTING</Typography></Divider></Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Rate Plan" value={form.rate_plan_id || ''}
                onChange={e => setForm({ ...form, rate_plan_id: e.target.value })}
                helperText="Determines sell rates and routing trunks for this customer">
                <MenuItem value=""><em>— No rate plan —</em></MenuItem>
                {ratePlans.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({p.currency || 'USD'})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Routing Profile" value={form.routing_profile_id || ''}
                onChange={e => setForm({ ...form, routing_profile_id: e.target.value })}
                helperText="Fallback routing profile when rate card has no trunk assigned">
                <MenuItem value=""><em>— No routing profile —</em></MenuItem>
                {routingProfiles.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({p.strategy})</MenuItem>)}
              </TextField>
            </Grid>

            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">BILLING</Typography></Divider></Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Billing Type" value={form.billing_type} onChange={e => setForm({ ...form, billing_type: e.target.value })}>
                {['prepaid', 'postpaid', 'wholesale', 'retail'].map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Currency" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {['USD', 'EUR', 'GBP', 'AED', 'SAR', 'GBP'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Credit Limit" type="number" value={form.credit_limit}
                onChange={e => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">{form.currency}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['active', 'suspended', 'terminated'].map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Timezone" value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })}>
                {['UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Karachi'].map(tz => (
                  <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name || !form.code}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save Changes' : 'Create Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Balance Adjustment Dialog */}
      <BalanceDialog open={assignDialogOpen} customer={editing} onClose={() => setAssignDialogOpen(false)} onSaved={() => { setAssignDialogOpen(false); load(); }} />

      {/* Delete Confirm */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{editing?.name}</strong>? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const BalanceDialog: React.FC<{ open: boolean; customer: any; onClose: () => void; onSaved: () => void }> = ({ open, customer, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'credit' | 'debit'>('credit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!customer || !amount) return;
    setSaving(true); setError('');
    try {
      await api.patch(`/tenants/${customer.id}/balance`, { amount: parseFloat(amount), type });
      onSaved();
    } catch (e: any) { setError(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjust Balance — {customer?.name}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Current balance: <strong>{customer?.currency} {Number(customer?.balance || 0).toFixed(2)}</strong>
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField select fullWidth label="Type" value={type} onChange={e => setType(e.target.value as any)}>
              <MenuItem value="credit">Add Credit</MenuItem>
              <MenuItem value="debit">Deduct</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Amount" type="number" value={amount} onChange={e => setAmount(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">{customer?.currency}</InputAdornment> }} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !amount}>
          {saving ? <CircularProgress size={20} /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
  // Credentials dialog shown after creating a new customer
  const copyToClipboard = (text: string) => { navigator.clipboard.writeText(text).catch(() => {}); };

  const CredentialsDialog = () => (
    <Dialog open={credsDialog} onClose={() => setCredsDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Key color="success" /> Customer Portal Credentials Created
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>{credentials?.name}</strong> has been created. Share these login credentials with the customer.
          <br />The password will not be shown again — save it now.
        </Alert>
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, fontFamily: 'monospace', fontSize: 14 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Box><strong>Portal URL:</strong> https://esbc2.synergytalk.org</Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box><strong>Username:</strong> {credentials?.username}</Box>
            <IconButton size="small" onClick={() => copyToClipboard(credentials?.username)}><ContentCopy fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box><strong>Email:</strong> {credentials?.email}</Box>
            <IconButton size="small" onClick={() => copyToClipboard(credentials?.email)}><ContentCopy fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box><strong>Password:</strong> {credentials?.password}</Box>
            <IconButton size="small" onClick={() => copyToClipboard(credentials?.password)}><ContentCopy fontSize="small" /></IconButton>
          </Box>
        </Box>
        <Alert severity="warning" sx={{ mt: 2 }}>
          The customer can change their password after first login via the account menu.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => copyToClipboard(`URL: https://esbc2.synergytalk.org\nUsername: ${credentials?.username}\nPassword: ${credentials?.password}`)}
          startIcon={<ContentCopy />}>Copy All</Button>
        <Button variant="contained" onClick={() => setCredsDialog(false)}>Done</Button>
      </DialogActions>
    </Dialog>
  );

};
